<script setup lang="ts">
import type { MeasurementStatus } from '@/api/generator';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { computed, useId } from 'vue';
import type { ZeroMeasurementValidity } from './utils/useLatestZeroMeasurement';
import { zeroMeasurementButtonTexts } from './utils/zeroMeasurementButtonTexts';
import {
  BUTTON_CENTER as CENTER,
  BUTTON_SIZE as SIZE,
  PIE_RADIUS,
  type ZeroMeasurementButtonPhase,
} from './utils/zeroMeasurementStartButton';
import ZeroMeasurementStartButtonHub from './ZeroMeasurementStartButtonHub.vue';
import ZeroMeasurementStartButtonPie from './ZeroMeasurementStartButtonPie.vue';

/**
 * Round button that starts a zero measurement, sitting on the bridge between
 * the detector interface and its array. It is a white ring carrying the name
 * of the measurement along its top arc like the text of a seal, around a hub
 * made of the pie (ZeroMeasurementStartButtonPie: how much of the latest
 * zero measurement's validity is left, or the progress of a run) and what
 * sits on it (ZeroMeasurementStartButtonHub: the play icon, the check while
 * the zero measurement is valid, the percentage while it runs). The exact
 * remaining time, the status of a run in its long form and the action are in
 * the tooltip and the accessible name (see zeroMeasurementButtonTexts).
 *
 * The button is in one of three phases. Idle, pressing it starts a zero
 * measurement. Measuring (a progress is set), the whole button grows by a
 * third and its ring turns to frosted glass (the hub stays solid white), so
 * the tiles and the particles behind it show through softly, the ring text
 * cross-fades from the name to the status of the run, and pressing it asks to
 * cancel the run instead. Succeeded, the button holds its grown shape while
 * the check pops into the hub and the ring says completed, and cannot be
 * pressed; it reports when the check has settled, so the host can drop it
 * back to idle, where the grow, the frost and the ring text revert through
 * their usual transitions while the check stays.
 *
 * Blocked (the interface is busy, offline or has no array), the idle button
 * stays visible but greyed; its tooltip and its accessible name carry the
 * reason instead of the action, so hovering the greyed button says why.
 *
 * Inviting (idle, pressable, and no valid zero measurement yet or any more),
 * a blue glow breathes in the center of the pie (see the pie), since a zero
 * measurement is a precondition for the session: it asks to be pressed until
 * it is, or until pressing becomes impossible. The button itself holds still.
 */
const props = defineProps<{
  /** Validity of the latest zero measurement; none shows an empty pie. */
  validity?: ZeroMeasurementValidity;
  /**
   * Progress of a running zero measurement, 0 to 100. Set, the button shows
   * it in the hub and the wedge instead of the validity and cannot be pressed.
   */
  progress?: number;
  /**
   * Status of the running zero measurement, shown in short form on the ring
   * and in long form in the tooltip. Only read while `progress` is set; a run
   * without a status yet (the pending start) reads as Initializing.
   */
  status?: MeasurementStatus | null;
  /**
   * The run just succeeded: the check pops into the hub and the button
   * cannot be pressed, until it emits `settled`. A running progress wins
   * over it.
   */
  succeeded?: boolean;
  /**
   * Why a zero measurement cannot start right now. Set, the button is greyed
   * and the reason replaces the action in its tooltip and accessible name.
   * Ignored while running or succeeded.
   */
  blockedReason?: string;
}>();

const emits = defineEmits<{
  /** Idle: start a zero measurement. */
  click: [];
  /** Measuring: cancel the running zero measurement. */
  cancel: [];
  /** Succeeded: the check has finished its pop and its hold. */
  settled: [];
}>();

// A fresh run is the more current fact than the outcome of the last one.
const phase = computed<ZeroMeasurementButtonPhase>(() => {
  if (props.progress !== undefined) {
    return 'measuring';
  }
  return props.succeeded ? 'succeeded' : 'idle';
});
const measuring = computed(() => phase.value === 'measuring');
const completed = computed(() => phase.value === 'succeeded');
// A block only applies while nothing is running and no outcome is shown.
const blocked = computed(() => phase.value === 'idle' && !!props.blockedReason);
// Asks to be pressed: it can be, and there is no valid zero measurement.
const inviting = computed(() => phase.value === 'idle' && !blocked.value && !props.validity);

function onClick() {
  if (measuring.value) {
    emits('cancel');
  } else {
    emits('click');
  }
}

const progressFraction = computed(() => Math.min(1, Math.max(0, (props.progress ?? 0) / 100)));
const progressText = computed(() => `${Math.round(progressFraction.value * 100)}%`);

// Succeeded, the pie is full: the run has just filled it, and the host is
// still fetching the validity that will keep it full.
const pieFraction = computed(() => {
  if (measuring.value) {
    return progressFraction.value;
  }
  if (completed.value) {
    return 1;
  }
  return props.validity?.remainingFraction ?? 0;
});

// The check is in the hub after a successful run and as long as the latest
// zero measurement is valid.
const checked = computed(() => completed.value || (!measuring.value && !!props.validity));

const texts = computed(() =>
  zeroMeasurementButtonTexts({
    phase: phase.value,
    status: props.status,
    progressText: progressText.value,
    validity: props.validity,
    blockedReason: props.blockedReason,
  }),
);

// The label runs along an arc midway through the ring, from 7 o'clock over
// the top to 5 o'clock (300°, clockwise), and is centered on 12 o'clock. The
// text is set upright with its tops facing outward, as on the top of a seal.
const LABEL_RADIUS = (PIE_RADIUS + CENTER) / 2;
const LABEL_ARC_START_DEG = 210;
const LABEL_ARC_END_DEG = 150;
function pointOnRing(radius: number, clockwiseDegFrom12: number) {
  const angle = (clockwiseDegFrom12 * Math.PI) / 180;
  return { x: CENTER + radius * Math.sin(angle), y: CENTER - radius * Math.cos(angle) };
}
const labelArcStart = pointOnRing(LABEL_RADIUS, LABEL_ARC_START_DEG);
const labelArcEnd = pointOnRing(LABEL_RADIUS, LABEL_ARC_END_DEG);
const labelArcPath =
  `M ${labelArcStart.x} ${labelArcStart.y} ` +
  `A ${LABEL_RADIUS} ${LABEL_RADIUS} 0 1 1 ${labelArcEnd.x} ${labelArcEnd.y}`;
const labelArcId = `zero-measurement-label-arc-${useId()}`;
// The arc (141px) holds about this many characters of the tracked uppercase
// label (6.3px each in the app's font; "ZERO MEASUREMENT" takes 101px). A
// longer text, as a translation may be, is squeezed onto the arc's length
// instead of running past its ends.
const LABEL_ARC_LENGTH =
  (LABEL_RADIUS * Math.PI * (360 - LABEL_ARC_START_DEG + LABEL_ARC_END_DEG)) / 180;
const LABEL_MAX_CHARS = 22;
const ringLabelLength = computed(() =>
  texts.value.ringLabel.length > LABEL_MAX_CHARS ? LABEL_ARC_LENGTH : undefined,
);
</script>

<template>
  <!-- The tooltip hangs on the wrapper, not the button, so it still opens
       while the button is disabled. -->
  <Tooltip>
    <template #target="{ props: tooltipProps }">
      <span v-bind="tooltipProps" class="start-button-anchor">
        <button
          type="button"
          class="start-button"
          :class="{
            'start-button--blocked': blocked,
            'start-button--inviting': inviting,
            'start-button--measuring': measuring,
            'start-button--succeeded': completed,
          }"
          :aria-label="texts.label"
          :disabled="blocked || completed"
          data-testid="ZeroMeasurementStartButton"
          @click="onClick()"
        >
          <svg
            class="start-button__face"
            :width="SIZE"
            :height="SIZE"
            :viewBox="`0 0 ${SIZE} ${SIZE}`"
            aria-hidden="true"
          >
            <!-- the ring: white at rest, clear while the button is frosted glass -->
            <circle class="start-button__ring" :cx="CENTER" :cy="CENTER" :r="CENTER" />
            <ZeroMeasurementStartButtonPie
              :phase="phase"
              :fraction="pieFraction"
              :inviting="inviting"
            />
            <defs>
              <path :id="labelArcId" :d="labelArcPath" />
            </defs>
            <!-- the ring text cross-fades when the phase changes its wording -->
            <Transition name="label" mode="out-in">
              <text
                :key="texts.ringLabel"
                class="start-button__label"
                data-testid="ZeroMeasurementStartLabel"
              >
                <textPath
                  :href="`#${labelArcId}`"
                  startOffset="50%"
                  text-anchor="middle"
                  :textLength="ringLabelLength"
                  :lengthAdjust="ringLabelLength ? 'spacingAndGlyphs' : undefined"
                >
                  {{ texts.ringLabel }}
                </textPath>
              </text>
            </Transition>
          </svg>
          <ZeroMeasurementStartButtonHub
            :phase="phase"
            :checked="checked"
            :progressText="progressText"
            @settled="emits('settled')"
          />
        </button>
      </span>
    </template>
    <template #tooltip>
      <div v-for="line in texts.tooltipLines" :key="line">{{ line }}</div>
    </template>
  </Tooltip>
</template>

<style lang="scss" scoped>
@use '@/styles/mixins';

$hover-glide: 0.15s;
$label-fade: 0.3s;
$grow: 0.4s;
$measuring-scale: 1.2;
$frost-opacity: 0.28;
$frost-blur: 5px;
$frost-edge: rgba(255, 255, 255, 0);
$focus-ring: 2px;
$blocked-opacity: 0.5;
$size: 68px;
$label-font-size: 9px;

// Inline wrapper carrying the tooltip; sized to the button so nothing shifts.
.start-button-anchor {
  display: block;
  width: $size;
  height: $size;
}

// The button is exactly the ring: no padding, and at rest no background of
// its own (the SVG ring paints it white). A light shadow lifts the white
// ring off the bridge, so the circle reads as a button even while the pie is
// empty. On hover the button lifts a step further. While a run is under way
// the button itself is the frosted glass: a translucent white disc blurring
// what is behind it, while the SVG ring goes clear; see the measuring
// modifier.
.start-button {
  position: relative;
  display: block;
  width: $size;
  height: $size;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0);
  cursor: pointer;
  color: rgb(var(--v-theme-on-surface));
  @include mixins.elevation(1);
  transition:
    box-shadow $hover-glide ease,
    opacity $hover-glide ease,
    transform $grow ease,
    background-color $grow ease,
    backdrop-filter $grow ease;

  &:hover:not(:disabled) {
    @include mixins.elevation(2);
  }

  &:focus-visible {
    outline: $focus-ring solid rgb(var(--v-theme-primary));
    outline-offset: 0;
  }

  // Not pressable while blocked.
  &:disabled {
    cursor: default;
  }

  // Blocked (interface busy, offline, no array): stays visible but greyed, so
  // the pie and its remaining validity are still readable; no hover lift.
  &--blocked {
    opacity: $blocked-opacity;
  }

  // Measuring: grows around its center, text and all, and turns to frosted
  // glass; both revert after. Succeeded, it keeps the grown, frosted shape
  // while the check is shown, and cannot be pressed meanwhile.
  &--measuring,
  &--succeeded {
    transform: scale($measuring-scale);
    background-color: rgba(255, 255, 255, $frost-opacity);
    backdrop-filter: blur($frost-blur);
  }

  // The frosted button shows through where the ring was; a faint white
  // hairline at the edge keeps the glass's rim readable over the tiles.
  &--measuring &__ring,
  &--succeeded &__ring {
    fill: transparent;
    stroke: $frost-edge;
    stroke-width: 1;
  }

  &--succeeded {
    cursor: default;
  }

  &__face {
    display: block;
  }

  &__ring {
    fill: #fff;
    stroke: transparent;
    stroke-width: 1;
    transition:
      fill $grow ease,
      stroke $grow ease;
  }

  // Seal text along the top of the ring, subdued like the status line above
  // the pair; the tracking counters the crowding on the inside of the curve.
  &__label {
    font-size: $label-font-size;
    font-weight: 500;
    letter-spacing: 0.1em;
    fill: rgba(var(--v-theme-on-surface), 0.6);
    dominant-baseline: middle;
    user-select: none;
  }

  // Hover and focus live on the button, so the swaps they trigger in its
  // parts are set here: the wedge's grey steps darker (the white stays),
  // the percentage gives way to the stop icon, and the check to the play
  // icon, as long as the button can be pressed.
  &:hover:not(:disabled) :deep(.pie__wedge) {
    stroke: rgb(var(--v-theme-primary-lighten-4));
  }

  &:hover :deep(.hub__progress),
  &:focus-visible :deep(.hub__progress),
  &:hover:not(:disabled) :deep(.hub__check),
  &:focus-visible :deep(.hub__check) {
    opacity: 0;
  }

  &:hover :deep(.hub__stop),
  &:focus-visible :deep(.hub__stop),
  &:hover:not(:disabled) :deep(.hub__play),
  &:focus-visible :deep(.hub__play) {
    opacity: 1;
  }
}

// Ring text swap: the old wording fades out completely, then the new one
// fades in.
.label-enter-active,
.label-leave-active {
  transition: opacity $label-fade ease;
}

.label-enter-from,
.label-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .start-button,
  .start-button__ring,
  .label-enter-active,
  .label-leave-active {
    transition: none;
  }
}
</style>
